import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";
import type { CashFlow } from "@/api/dashboard";

/**
 * Six months of money in and money out (ADR 0003).
 *
 * Every rule this follows is written down in that ADR, because the skill
 * `project.md` §5.6 names does not exist in this environment. The ones visible
 * here:
 *
 * - **Money in is the accent, money out is a neutral. Neither is red.** Red is
 *   reserved strictly for overspend (§5.5), and every month has spending in it.
 * - **Colour is never the only carrier.** In and out sit on opposite sides of a
 *   shared baseline, so the chart reads identically in greyscale.
 * - **The numbers are in the DOM as text.** Each bar pair carries an
 *   `aria-label` stating both figures; delete the SVG and the screen still
 *   answers the question.
 * - **The axis is continuous.** A month with nothing in it arrives as a zero
 *   from the server and keeps its place — closing the gap would put January
 *   beside March and label it six months.
 */
export function CashFlowChart({
  history,
  currency,
}: {
  history: CashFlow[];
  currency: string;
}) {
  // The tallest bar decides the scale. Both halves share it, so a month that
  // earned €3,000 and spent €300 looks like what it is.
  const t = useT();
  const format = useFormat();
  const tallest = Math.max(
    1,
    ...history.map((m) => Math.max(m.in_minor, Math.abs(m.out_minor))),
  );

  return (
    <div>
      <div className="flex items-end justify-between gap-2" data-testid="cashflow-chart">
        {history.map((month) => {
          const inHeight = (month.in_minor / tallest) * 100;
          const outHeight = (Math.abs(month.out_minor) / tallest) * 100;

          return (
            <div key={month.period_start} className="flex flex-1 flex-col items-center gap-1">
              <div
                className="flex h-24 w-full flex-col justify-end gap-px"
                role="img"
                aria-label={t("dashboard.cashflowMonthLabel", {
                  month: format.shortMonth(month.period_start),
                  in: format.amount(month.in_minor, currency),
                  out: format.amount(-month.out_minor, currency),
                })}
                data-testid="cashflow-month"
              >
                {/* Money in, growing up from the baseline. */}
                <div className="flex h-1/2 w-full items-end">
                  <div
                    className="w-full rounded-t-sm bg-accent"
                    style={{ height: `${Math.max(inHeight / 2, month.in_minor > 0 ? 2 : 0)}%` }}
                  />
                </div>
                {/* Money out, hanging below it. Muted, never red. */}
                <div className="flex h-1/2 w-full items-start">
                  <div
                    className="w-full rounded-b-sm bg-chart-out"
                    style={{
                      height: `${Math.max(outHeight / 2, month.out_minor < 0 ? 2 : 0)}%`,
                    }}
                  />
                </div>
              </div>
              <span
                className={`text-xs ${month.is_complete ? "text-muted" : "font-medium text-ink"}`}
              >
                {format.shortMonth(month.period_start)}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-2 flex items-center gap-4 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-sm bg-accent" aria-hidden="true" />
          {t("dashboard.moneyIn")}
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2 w-2 rounded-sm bg-chart-out" aria-hidden="true" />
          {t("dashboard.moneyOut")}
        </span>
      </p>
    </div>
  );
}
